import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingDown, AlertTriangle, CheckCircle, Layers, Package,
  Settings, RefreshCw, ArrowRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  critical: { label: 'CRITICAL', color: 'text-red-300', bg: 'bg-red-900/50 border-red-700', icon: AlertTriangle },
  below_reorder: { label: 'Below Reorder', color: 'text-amber-300', bg: 'bg-amber-900/50 border-amber-700', icon: TrendingDown },
  healthy: { label: 'Healthy', color: 'text-green-300', bg: 'bg-green-900/40 border-green-700', icon: CheckCircle },
  overstock: { label: 'Overstock', color: 'text-blue-300', bg: 'bg-blue-900/40 border-blue-700', icon: Package },
  no_data: { label: 'No Data', color: 'text-zinc-500', bg: 'bg-zinc-800 border-zinc-700', icon: Sparkles },
};

export default function ReorderSuggestions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('raw');
  const [statusFilter, setStatusFilter] = useState('all');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [applying, setApplying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reorder/suggestions`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reorder data');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const applyReorder = async (row) => {
    setApplying(row.id);
    try {
      await axios.post(`${API}/reorder/apply`, {
        kind: row.kind, id: row.id,
        suggested_reorder_point: row.suggested_reorder_point,
      }, { withCredentials: true });
      toast.success(`Min-stock updated for ${row.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(null); }
  };

  const rows = tab === 'raw' ? (data?.raw_materials || []) : (data?.finished_goods || []);
  const filtered = statusFilter === 'all' ? rows : rows.filter(r => r.status === statusFilter);

  return (
    <div data-testid="reorder-page" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <Sparkles className="text-violet-400" size={24} /> Smart Reorder Points
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            AI-suggested min-stock based on last {data?.settings?.window_days || 90}-day velocity × (lead-time + safety buffer).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reorder-settings-btn"
            onClick={() => setSettingsOpen(true)}
            className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700 flex items-center gap-1.5"
          ><Settings size={13} /> Settings</button>
          <button
            data-testid="reorder-refresh"
            onClick={load}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
          ><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="RM Critical" value={data?.kpi?.raw_critical || 0} testid="kpi-raw-critical" color="text-red-400" />
        <KPI label="RM Below Reorder" value={data?.kpi?.raw_below_reorder || 0} testid="kpi-raw-below" color="text-amber-400" />
        <KPI label="FG Critical" value={data?.kpi?.fg_critical || 0} testid="kpi-fg-critical" color="text-red-400" />
        <KPI label="FG Below Reorder" value={data?.kpi?.fg_below_reorder || 0} testid="kpi-fg-below" color="text-amber-400" />
      </div>

      {/* Formula banner */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3 text-xs text-zinc-400 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-zinc-300">reorder_point</span>
        <span>=</span>
        <span className="font-mono text-zinc-300">avg_daily_use × (lead_time + safety_buffer)</span>
        <span className="text-zinc-500 ml-2">· defaults: lead {data?.settings?.default_lead_days || 7}d, safety {data?.settings?.default_safety_days || 7}d, window {data?.settings?.window_days || 90}d</span>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <TabBtn active={tab === 'raw'} onClick={() => setTab('raw')} testid="reorder-tab-raw">
          <Layers size={14} /> Raw Materials ({(data?.raw_materials || []).length})
        </TabBtn>
        <TabBtn active={tab === 'fg'} onClick={() => setTab('fg')} testid="reorder-tab-fg">
          <Package size={14} /> Finished Goods ({(data?.finished_goods || []).length})
        </TabBtn>
        <div className="flex-1" />
        <select
          data-testid="reorder-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded-lg px-3 py-1.5"
        >
          <option value="all">All statuses</option>
          <option value="critical">Critical only</option>
          <option value="below_reorder">Below reorder</option>
          <option value="healthy">Healthy</option>
          <option value="overstock">Overstock</option>
          <option value="no_data">No data</option>
        </select>
      </div>

      {/* Suggestions table */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm animate-pulse">Analyzing consumption data…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No rows match the current filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-zinc-300">
              <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-right">Current Stock</th>
                  <th className="px-3 py-2 text-right">Avg Daily Use</th>
                  <th className="px-3 py-2 text-right">{tab === 'raw' ? 'Lead+Safety' : 'Lead+Safety'}</th>
                  <th className="px-3 py-2 text-right">Existing Min</th>
                  <th className="px-3 py-2 text-right">Suggested</th>
                  <th className="px-3 py-2 text-right">Δ</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.no_data;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`reorder-row-${r.id}`}
                      className="border-t border-zinc-800 hover:bg-zinc-800/40"
                    >
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border ${meta.bg} ${meta.color}`}>
                          <Icon size={10} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-zinc-100">{r.name || '—'}</td>
                      <td className="px-3 py-2 text-right text-zinc-100 font-semibold">{r.current_stock?.toLocaleString('en-IN')} <span className="text-zinc-500 text-[10px]">{r.unit}</span></td>
                      <td className="px-3 py-2 text-right text-zinc-400">{r.avg_daily_consumption?.toFixed(3)} <span className="text-zinc-500 text-[10px]">/day</span></td>
                      <td className="px-3 py-2 text-right text-zinc-400">{r.lead_days}+{r.safety_days}d <span className="text-zinc-500 text-[10px]">({r.lead_source})</span></td>
                      <td className="px-3 py-2 text-right text-zinc-400">{r.existing_min_stock}</td>
                      <td className="px-3 py-2 text-right text-violet-300 font-bold">{r.suggested_reorder_point}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${r.delta > 0 ? 'text-red-400' : r.delta < 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                        {r.delta > 0 ? `+${r.delta}` : r.delta}
                      </td>
                      <td className="px-3 py-2">
                        {r.delta !== 0 && r.suggested_reorder_point > 0 ? (
                          <button
                            data-testid={`reorder-apply-${r.id}`}
                            onClick={() => applyReorder(r)}
                            disabled={applying === r.id}
                            className="px-2 py-1 rounded-md bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 text-white text-[10px] font-medium flex items-center gap-1"
                          >
                            {applying === r.id ? 'Applying…' : (<><ArrowRight size={10} /> Apply</>)}
                          </button>
                        ) : <span className="text-zinc-600 text-[10px]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {settingsOpen && data?.settings && (
        <SettingsModal
          settings={data.settings}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => { setSettingsOpen(false); load(); }}
        />
      )}
    </div>
  );
}

const KPI = ({ label, value, testid, color }) => (
  <div data-testid={testid} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
    <div className="text-xs text-zinc-400">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color || 'text-zinc-100'}`}>{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${
      active ? 'bg-violet-600 border-violet-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
    }`}
  >{children}</button>
);

function SettingsModal({ settings, onClose, onSaved }) {
  const [form, setForm] = useState({
    default_lead_days: settings.default_lead_days,
    default_safety_days: settings.default_safety_days,
    window_days: settings.window_days,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/reorder/settings`, form, { withCredentials: true });
      toast.success('Settings updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="reorder-settings-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-zinc-100">Reorder Settings</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-200">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <NumField label="Default Lead Time (days)" value={form.default_lead_days} onChange={v => setForm({ ...form, default_lead_days: v })} testid="cfg-lead" />
          <NumField label="Safety Buffer (days)" value={form.default_safety_days} onChange={v => setForm({ ...form, default_safety_days: v })} testid="cfg-safety" />
          <NumField label="Consumption Window (days)" value={form.window_days} onChange={v => setForm({ ...form, window_days: v })} testid="cfg-window" />
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm">Cancel</button>
            <button data-testid="cfg-save" type="submit" disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 text-white text-sm font-medium">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs text-zinc-400">{label}</label>
    <input
      data-testid={testid}
      type="number" min={1} max={365}
      value={value}
      onChange={e => onChange(parseInt(e.target.value || '0'))}
      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
    />
  </div>
);
